/**
 * The pan/zoom/fit engine — SPEC.md §3, §9, §12 M2; issue #2 "Implementation
 * Decisions", issue #4.
 *
 * `render.ts`'s sibling: a pure, dependency-free store. No DOM, pointer, or
 * ResizeObserver here — the calling component turns real events into these
 * plain-number calls, the same way `mermaidRender.ts` drives `render.ts`.
 */

export interface ViewportState {
  /** 0.12–6.00 (12%–600%). */
  scale: number;
  /** Pan offset, container-space pixels. */
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface Viewport {
  getState(): ViewportState;
  subscribe(listener: (state: ViewportState) => void): () => void;
  pan(dx: number, dy: number): void;
  zoomBy(factor: number, anchor: Point): void;
  zoomTo(scale: number, anchor: Point): void;
  fit(content: Size, container: Size): void;
  onRenderSuccess(content: Size, container: Size): void;
  onContainerResize(content: Size, container: Size): void;
  /** Re-arms the one-shot `onRenderSuccess` guard — called when the buffer itself is wholesale-replaced (New, Recent, drag-drop, hash load), so *that* diagram's first render fits too, not just the session's very first one. */
  resetFitGuard(): void;
  destroy(): void;
}

const INITIAL_STATE: ViewportState = { scale: 1, x: 0, y: 0 };
const MIN_SCALE = 0.12;
const MAX_SCALE = 6;
/** Fixed margin fit leaves around the content, in container-space pixels. */
const FIT_PADDING = 48;

function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/** The largest content-in-container scale, clamped and centered. */
function computeFit(content: Size, container: Size): ViewportState {
  const availableWidth = container.width - FIT_PADDING * 2;
  const availableHeight = container.height - FIT_PADDING * 2;
  const scale = clampScale(Math.min(availableWidth / content.width, availableHeight / content.height));
  return {
    scale,
    x: (container.width - content.width * scale) / 2,
    y: (container.height - content.height * scale) / 2,
  };
}

/**
 * Solves for the pan offset that keeps `anchor` (container-space — the
 * cursor or pinch-centre) pointing at the same content-space location before
 * and after the scale change, so zooming doesn't fling the diagram out from
 * under the cursor.
 */
function zoomAtAnchor(state: ViewportState, newScale: number, anchor: Point): ViewportState {
  const contentX = (anchor.x - state.x) / state.scale;
  const contentY = (anchor.y - state.y) / state.scale;
  return {
    scale: newScale,
    x: anchor.x - contentX * newScale,
    y: anchor.y - contentY * newScale,
  };
}

export function createViewport(): Viewport {
  let state: ViewportState = INITIAL_STATE;
  let destroyed = false;
  let hasFittedOnce = false;

  const listeners = new Set<(state: ViewportState) => void>();

  function emit(patch: Partial<ViewportState>) {
    if (destroyed) return;
    state = { ...state, ...patch };
    for (const listener of listeners) listener(state);
  }

  return {
    getState() {
      return state;
    },

    subscribe(listener) {
      listeners.add(listener);
      listener(state);
      return () => listeners.delete(listener);
    },

    pan(dx, dy) {
      emit({ x: state.x + dx, y: state.y + dy });
    },

    zoomBy(factor, anchor) {
      const newScale = clampScale(state.scale * factor);
      emit(zoomAtAnchor(state, newScale, anchor));
    },

    zoomTo(scale, anchor) {
      emit(zoomAtAnchor(state, clampScale(scale), anchor));
    },

    fit(content, container) {
      emit(computeFit(content, container));
    },

    onRenderSuccess(content, container) {
      if (hasFittedOnce) return;
      hasFittedOnce = true;
      emit(computeFit(content, container));
    },

    onContainerResize(content, container) {
      emit(computeFit(content, container));
    },

    resetFitGuard() {
      hasFittedOnce = false;
    },

    destroy() {
      destroyed = true;
      listeners.clear();
    },
  };
}
